// authSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { authServie } from "../../services/authService";
import { toast } from "react-toastify";

import { ACTIVATE_ACCOUNT_RESPONSE_CODES, ATP_LOGIN, USERS_TYPE } from "../../data/Constants";
import { handleCurrentStep } from "./userInfoStepperSlice";

// initial state for authentication
const initialState = {
    user: null,
    loading: false,
    resendLoading: false,
    emailSent: false,
    error: null,
    isUserVerified: false,
    active: false,
    percent_calc: {
        dob: null,
        race: null,
        address_1: null,
        city_id: null,
        mobile_telephone: null,
        work_experiences: null,
        technology_field: null,
        technology_sub_field: null,
        profile_path: null,
        qualifications: null,
    },
    completed_profile_percentage: 0,
};

// An asynchronous thunk for the registration process
export const registerUser = createAsyncThunk("user/register", async ({ newUser, navigate }) => {
    try {
        const response = await authServie.registerUser(newUser);
        toast.success(response?.data?.message);
        navigate("/email-verification", {
            state: {
                name: newUser?.name,
                email: newUser?.email,
            },
        });
    } catch (error) {
        toast.error(error?.response?.data?.message);
    }
});

// An asynchronous thunk for the registered account verification process
export const activateAccount = createAsyncThunk("user/activate-account", async ({ token, navigate }, { rejectWithValue }) => {
    try {
        const response = await authServie.activateAccount(token);
        toast.success(response?.data?.message);
        navigate(`${response?.data?.type === USERS_TYPE.USER ? "/" : ATP_LOGIN}`);
    } catch (error) {
        toast.error(error?.response?.data?.message);
        return rejectWithValue(error?.response?.data);
    }
});

// An asynchronous thunk for the resend email
export const resendEmail = createAsyncThunk("user/resend-email", async ({ data, navigate }, { rejectWithValue }) => {
    try {
        const response = await authServie.resendEmail(data);
        toast.success(response?.data?.message);
    } catch (error) {
        const responseData = error?.response?.data;
        toast.error(responseData?.message);
        if (responseData?.code === ACTIVATE_ACCOUNT_RESPONSE_CODES.ACCOUNT_ALREADY_VERIFIED) {
            navigate("/");
        }
        return rejectWithValue(responseData);
    }
});

// An asynchronous thunk for the login
export const login = createAsyncThunk("user/login", async ({ user, navigate, dispatch }, { rejectWithValue }) => {
    try {
        const response = await authServie.login(user);
        const responseData = response?.data;
        // if (responseData?.data?.user_type === USERS_TYPE.USER) {
        //     dispatch(handleCurrentStep(1))
        // }
        toast.success(responseData?.message);
        localStorage.setItem("token", responseData?.data?.token);
        navigate(`${responseData?.data?.user_type === USERS_TYPE.ATP_USER ? "/atp/dashboard" : "/"}`);
        return responseData?.data;
    } catch (error) {
        const responseData = error?.response?.data;
        toast.error(responseData?.message);
        return rejectWithValue(responseData);
    }
});

// An asynchronous thunk for the reset password
export const forgotPassword = createAsyncThunk("user/forgotPassword", async ({ data, navigate }, { rejectWithValue }) => {
    try {
        const response = await authServie.forgotPassword(data);
        const responseData = response?.data;
        toast.success(responseData?.message);
    } catch (error) {
        const responseData = error?.response?.data;
        toast.error(responseData?.message);
    }
});

// An asynchronous thunk for the reset password
export const resetPassword = createAsyncThunk("user/resetPassword", async ({ data, token, navigate }, { rejectWithValue }) => {
    try {
        const response = await authServie.resetPassword({ data, token });
        const responseData = response?.data;
        toast.success(responseData?.message);
        navigate(`${responseData?.data?.user_type === USERS_TYPE.USER ? "/" : ATP_LOGIN}`);
    } catch (error) {
        const responseData = error?.response?.data;
        toast.error(responseData?.message);
        navigate("/");
    }
});

// An asynchronous thunk for the deactivate forgot password
export const deactivateForgotPassword = createAsyncThunk("user/deactivateForgotPassword", async ({ token, navigate }, { rejectWithValue }) => {
    try {
        const response = await authServie.deactivateForgotPassword(token);
        const responseData = response?.data;
        toast.success(responseData?.message);
        navigate("/");
    } catch (error) {
        const responseData = error?.response?.data;
        toast.error(responseData?.message);
        navigate("/");
    }
});

export const adminLogin = createAsyncThunk("user/adminLogin", async ({ email, token, navigate, dispatch }, { rejectWithValue }) => {
    try {
        dispatch(logoutUser());
        localStorage.clear();
        const response = await authServie.adminLogin({ email, token });
        const responseData = response?.data;
        toast.success(responseData?.message);
        localStorage.setItem("token", responseData?.data?.token);
        navigate(`${responseData?.data?.user_type === USERS_TYPE.ATP_USER ? "/atp/dashboard" : "/dashboard"}`);
        return responseData?.data;
    } catch (error) {
        const responseData = error?.response?.data;
        toast.error(responseData?.message);
        return rejectWithValue(responseData);
    }
});

export const verifyOrganization = createAsyncThunk("user/verifyOrganization", async ({ id }, { rejectWithValue }) => {
    try {
        const response = await authServie.verifyOrganization(id);
        return response;
    } catch (error) {
        toast.error(error?.response?.data?.message);
    }
});

// Create the authentication slice
const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        logoutUser: (state, action) => {
            state.user = null;
            state.active = false;
            localStorage.removeItem("token");
            // sessionStorage.clear();
        },
        handleUserActiveStatus: (state, action) => {
            state.active = action.payload;
        },
        handleProfileImage: (state, action) => {
            state.user.profile_path = action.payload;
        },
        handlePreferredReg: (state, action) => {
            state.user.preferred_registration = action.payload;
        },
        handleIdentityNum: (state, action) => {
            state.user.identity_number = action.payload;
        },
        handleTechField: (state, action) => {
            state.user.technology_field = action.payload;
        },
        updateQualExpPercentageToZero: (state, action) => {
            action.payload.forEach((value) => {
                state.percent_calc[value] = 0;
            });
            let percentage = 0;
            percentage += state.percent_calc?.dob ? 5 : 0;
            percentage += state.percent_calc?.race ? 4 : 0;
            percentage += state.percent_calc?.address_1 ? 4 : 0;
            percentage += state.percent_calc?.city_id ? 4 : 0;
            percentage += state.percent_calc?.mobile_telephone ? 4 : 0;
            percentage += state.percent_calc?.work_experiences ? 4 : 0;
            percentage += state.percent_calc?.technology_field ? 13 : 0;
            percentage += state.percent_calc?.technology_sub_field ? 12 : 0;
            percentage += state.percent_calc?.profile_path ? 25 : 0;
            percentage += state.percent_calc?.qualifications ? 25 : 0;
            state.user.completed_profile_percentage = percentage;
        },
        updateProfileCompletionStatus: (state, action) => {
            action.payload.forEach((value) => {
                state.percent_calc[value] = 1;
            });
            let percentage = 0;
            percentage += state.percent_calc?.dob ? 5 : 0;
            percentage += state.percent_calc?.race ? 4 : 0;
            percentage += state.percent_calc?.address_1 ? 4 : 0;
            percentage += state.percent_calc?.city_id ? 4 : 0;
            percentage += state.percent_calc?.mobile_telephone ? 4 : 0;
            percentage += state.percent_calc?.work_experiences ? 4 : 0;
            percentage += state.percent_calc?.technology_field ? 13 : 0;
            percentage += state.percent_calc?.technology_sub_field ? 12 : 0;
            percentage += state.percent_calc?.profile_path ? 25 : 0;
            percentage += state.percent_calc?.qualifications ? 25 : 0;
            state.user.completed_profile_percentage = percentage;
        },
        handleAtpProfileCompletion: (state, action) => {
            state.user.is_profile_completed = action.payload;
        },
        handleAtpProfileStatus: (state, action) => {
            state.user.status = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(registerUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(registerUser.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload;
            })
            .addCase(registerUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })
            .addCase(activateAccount.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(activateAccount.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.isUserVerified = true;
            })
            .addCase(activateAccount.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                // Check if user is verified and the rejection reason indicates certain conditions
                if (
                    state.isUserVerified === true &&
                    (action.payload?.code === ACTIVATE_ACCOUNT_RESPONSE_CODES.LINK_EXPIRED ||
                        action.payload?.code === ACTIVATE_ACCOUNT_RESPONSE_CODES.INVALID_LINK ||
                        action.payload?.code === ACTIVATE_ACCOUNT_RESPONSE_CODES.ACCOUNT_ALREADY_VERIFIED)
                ) {
                    state.isUserVerified = true; // Keep user as verified
                } else {
                    state.isUserVerified = false; // Mark user as not verified if other conditions apply
                }
            })
            .addCase(resendEmail.pending, (state, action) => {
                state.resendLoading = true;
                state.error = null;
            })
            .addCase(resendEmail.fulfilled, (state, action) => {
                state.resendLoading = false;
                state.error = null;
                state.emailSent = true;
            })
            .addCase(resendEmail.rejected, (state, action) => {
                state.resendLoading = false;
                state.error = action.payload;
                state.emailSent = false;
                // Check if user is verified and the rejection reason indicates certain conditions
                if (
                    state.isUserVerified === true &&
                    (action.payload?.code === ACTIVATE_ACCOUNT_RESPONSE_CODES.ACCOUNT_ALREADY_VERIFIED || action.payload?.code === ACTIVATE_ACCOUNT_RESPONSE_CODES.INVALID_LINK)
                ) {
                    state.isUserVerified = true; // Keep user as verified
                } else {
                    state.isUserVerified = false; // Mark user as not verified if other conditions apply
                }
            })
            .addCase(login.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(login.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload;
                state.active = true;
                const data = action.payload;
                state.percent_calc = {
                    dob: data?.dob_count === true ? 1 : 0,
                    race: data?.race_count === true ? 1 : 0,
                    address_1: data?.address_1_count === true ? 1 : 0,
                    city_id: data?.city_id_count === true ? 1 : 0,
                    mobile_telephone: data?.mobile_telephone_count === true ? 1 : 0,
                    work_experiences: data?.work_experiences_count === true ? 1 : 0,
                    technology_field: data?.technology_field_count === true ? 1 : 0,
                    technology_sub_field: data?.technology_sub_field_count === true ? 1 : 0,
                    profile_path: data?.profile_path_count === true ? 1 : 0,
                    qualifications: data?.qualifications_count === true ? 1 : 0,
                };
                state.user.completed_profile_percentage = data?.percentage;
                localStorage.setItem("logoutTimer", 60);
            })
            .addCase(login.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })
            .addCase(forgotPassword.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(forgotPassword.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(forgotPassword.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })
            .addCase(resetPassword.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(resetPassword.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(resetPassword.rejected, (state, action) => {
                state.loading = false;
            })
            .addCase(deactivateForgotPassword.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deactivateForgotPassword.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(deactivateForgotPassword.rejected, (state, action) => {
                state.loading = false;
            })
            .addCase(adminLogin.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(adminLogin.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload;
                state.active = true;
                const data = action.payload;
                state.percent_calc = {
                    dob: data?.dob_count === true ? 1 : 0,
                    race: data?.race_count === true ? 1 : 0,
                    address_1: data?.address_1_count === true ? 1 : 0,
                    city_id: data?.city_id_count === true ? 1 : 0,
                    mobile_telephone: data?.mobile_telephone_count === true ? 1 : 0,
                    work_experiences: data?.work_experiences_count === true ? 1 : 0,
                    technology_field: data?.technology_field_count === true ? 1 : 0,
                    technology_sub_field: data?.technology_sub_field_count === true ? 1 : 0,
                    profile_path: data?.profile_path_count === true ? 1 : 0,
                    qualifications: data?.qualifications_count === true ? 1 : 0,
                };
                state.user.completed_profile_percentage = data?.percentage;
                localStorage.setItem("logoutTimer", 60);
            })
            .addCase(adminLogin.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })
            .addCase(verifyOrganization.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(verifyOrganization.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(verifyOrganization.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            });
    },
});

// Export the authentication actions and reducer
export const {
    logoutUser,
    handleUserActiveStatus,
    handleProfileImage,
    handlePreferredReg,
    updateProfileCompletionStatus,
    updateQualExpPercentageToZero,
    handleAtpProfileCompletion,
    handleIdentityNum,
    handleTechField,
    handleAtpProfileStatus,
} = authSlice.actions;
export default authSlice.reducer;
